import { AlertTriangle, Award, MessageSquareWarning, Mail, Phone, PhoneOff, TrendingDown, TrendingUp, Users } from 'lucide-react'
import type { DashboardSummary, FacilityAdministratorContact, TierDistribution } from '../index.js'
import { FacilityAdministratorIcon } from '../../core/index.js'
import { TierDistributionBars } from './TierDistributionBars.js'
import { EndpointHint } from '../../core/index.js'

function Stat({ icon, value, label, sub, tone = 'plain' }: { icon: React.ReactNode; value: string; label: string; sub?: string; tone?: 'good' | 'warn' | 'plain' }) {
  const toneClass = tone === 'good' ? 'text-green' : tone === 'warn' ? 'text-coral' : 'text-ink'
  return (
    <div className="flex items-start gap-3 rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0">
        <div className={`text-xl font-black leading-tight ${toneClass}`}>{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
        {sub && <div className="text-[10px] text-ink-mid">{sub}</div>}
      </div>
    </div>
  )
}

/**
 * Who runs the facility: each administrator's name with their phone and
 * email as links, so anyone looking at the facility can reach them. An
 * administrator whose invitation still waits is marked. Shown on
 * Configuration › Details to a facility administrator, who cannot manage them.
 */
export function FacilityAdministratorContacts({ administrators }: { administrators: readonly FacilityAdministratorContact[] }) {
  return (
    <div className="rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-ink-mid">
        <FacilityAdministratorIcon className="h-3.5 w-3.5" />
        {administrators.length === 1 ? 'Facility administrator' : 'Facility administrators'}
      </div>
      {administrators.length === 0 ? (
        <div className="inline-flex items-center gap-1.5 rounded border border-gold bg-gold/20 px-2 py-1 text-xs font-bold text-ink">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-ink" />
          No facility administrator yet
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {administrators.map((administrator) => (
            <li key={administrator.administratorId} className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 text-sm font-black leading-tight text-ink">
                {administrator.name}
                {administrator.invited && <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-ink">invited · waiting</span>}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-ink-mid">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3 shrink-0" />
                  {administrator.phone && /\d/.test(administrator.phone) ? (
                    <a href={`tel:${administrator.phone.replace(/[^+\d]/g, '')}`} className="text-marine hover:text-marine-dk">
                      {administrator.phone}
                    </a>
                  ) : administrator.phone ? (
                    <span>{administrator.phone}</span>
                  ) : (
                    <span>No phone recorded</span>
                  )}
                </span>
                <a href={`mailto:${administrator.email}`} className="inline-flex min-w-0 items-center gap-1 text-marine hover:text-marine-dk">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{administrator.email}</span>
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** The roster's head count by employment type, and how many have a last day still to come. */
export interface RosterMix {
  fullTime: number
  partTime: number
  prn: number
  agency: number
  leaving: number
}

interface RosterHighlightsProps {
  summary: DashboardSummary
  tiers: TierDistribution['tiers']
  mix: RosterMix
  /** Workers below Gold whose running score would make them Gold at the close. */
  onTrackForGold: number
  /** Barrier notes from workers not yet seen by staff. */
  unseenNotes: number
}

function mixText(mix: RosterMix): string {
  const parts = [
    mix.fullTime > 0 ? `${mix.fullTime} full time` : null,
    mix.partTime > 0 ? `${mix.partTime} part time` : null,
    mix.prn > 0 ? `${mix.prn} PRN` : null,
    mix.agency > 0 ? `${mix.agency} agency` : null,
    mix.leaving > 0 ? `${mix.leaving} leaving` : null,
  ].filter((part): part is string => part !== null)
  return parts.join(' · ')
}

/**
 * The Roster tab's summary, as the mockup draws it (screen 8): the roster's reliability in bold strokes - head count by
 * employment type, the Gold share with who is on track for it, the average score against last month, the call-outs,
 * the notes not yet seen - then the tiers held. It replaced "This facility at a glance" (25 October 2026), whose
 * warnings, open gaps and internal-bid share the Today and Month tabs already show.
 */
export function RosterHighlights({ summary, tiers, mix, onTrackForGold, unseenNotes }: RosterHighlightsProps) {
  const delta = summary.avgReliabilityScoreDeltaPoints
  const DeltaIcon = delta !== null && delta < 0 ? TrendingDown : TrendingUp
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[10px] font-bold uppercase tracking-wide text-marine">
        The roster at a glance
        <EndpointHint>GET /facilities/{'{facilityId}'}/dashboard/summary</EndpointHint>
      </div>
      <div className="grid gap-2 grid-cols-2 lg:grid-cols-5">
        <Stat icon={<Users className="h-5 w-5" />} value={String(summary.activeEmployees)} label="On the roster" sub={mixText(mix) || undefined} />
        <Stat icon={<Award className="h-5 w-5" />} value={`${summary.goldTierPct}%`} label="At Gold" sub={`${summary.goldTierCount} ${summary.goldTierCount === 1 ? 'worker' : 'workers'}${onTrackForGold > 0 ? ` · ${onTrackForGold} more on track` : ''}`} tone={summary.goldTierPct >= 30 ? 'good' : 'plain'} />
        <Stat
          icon={<DeltaIcon className="h-5 w-5" />}
          value={String(summary.avgReliabilityScore)}
          label="Average score"
          sub={delta === null ? 'no prior period yet' : `${delta >= 0 ? '+' : ''}${delta} pts vs. last month`}
          tone={delta === null ? 'plain' : delta >= 0 ? 'good' : 'warn'}
        />
        <Stat
          icon={<PhoneOff className="h-5 w-5" />}
          value={String(summary.callOutsThisMonth)}
          label="Call-outs this month"
          sub={summary.callOutsDeltaPct === null ? undefined : `${summary.callOutsDeltaPct >= 0 ? '+' : ''}${summary.callOutsDeltaPct}% vs. prior`}
          tone={summary.callOutsThisMonth === 0 ? 'good' : summary.callOutsDeltaPct !== null && summary.callOutsDeltaPct > 0 ? 'warn' : 'plain'}
        />
        <Stat icon={<MessageSquareWarning className="h-5 w-5" />} value={String(unseenNotes)} label="Notes from workers" sub="not yet seen" tone={unseenNotes > 0 ? 'warn' : 'plain'} />
      </div>
      <div className="rounded-lg border border-marine/15 bg-white px-4 py-3">
        <TierDistributionBars tiers={tiers} />
      </div>
    </section>
  )
}
